import { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/personnel-types/PersonnelTypesAction";
import * as uiHelpers from "../PersonnelTypesUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { usePersonnelTypesUIContext } from "../PersonnelTypesUIContext";

export function PersonnelTypesTable({ deletePersonnelTypes }) {
  // PersonnelTypes UI Context
  const personnelTypesUIContext = usePersonnelTypesUIContext();
  const personnelTypesUIProps = useMemo(() => {
    return {
      ids: personnelTypesUIContext.ids,
      setIds: personnelTypesUIContext.setIds,
      queryParams: personnelTypesUIContext.queryParams,
      setQueryParams: personnelTypesUIContext.setQueryParams,
      openEditPersonnelTypePage: personnelTypesUIContext.openEditPersonnelTypePage
    };
  }, [personnelTypesUIContext]);

  // Getting curret state of PersonnelTypes list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.personnelTypes }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);
  
  // PersonnelTypes Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(actions.getPersonnelTypes(personnelTypesUIProps.queryParams));
    return () => dispatch(actions.cleanPersonnelTypesState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personnelTypesUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "name",
      text: "Adı",
      sort: true,
      sortCaret: sortCaret,
    },
    {
      dataField: "definition",
      text: "Tanımı",
      sort: true,
      sortCaret: sortCaret,
    }
  ];

  const actionColumn = {
    dataField: "action",
    text: "İşlemler",
    formatter: columnFormatters.ActionsColumnFormatter,
    formatExtraData: {
      isEditionAuthorized,
      isDeletionAuthorized,
      openEditPersonnelTypePage: personnelTypesUIProps.openEditPersonnelTypePage,
      deletePersonnelTypes
    },
    classes: "text-right pr-0",
    headerClasses: "text-right pr-3",
    style: {
      minWidth: "100px",
    }
  }

  if (isEditionAuthorized || isDeletionAuthorized) columns.push(actionColumn);

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: personnelTypesUIProps.queryParams.pageSize,
    page: personnelTypesUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? []}
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                personnelTypesUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: personnelTypesUIProps.ids,
                setIds: personnelTypesUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}